import { piiScrub, piiScrubDeep, piiScrubStack } from '@kit/shared/pii-scrub';

/**
 * PostHog-Privacy-Helpers (#479, DSGVO).
 *
 * Locari verarbeitet Bewerber-PII (DSK-Sonderkategorie). Diese Helpers sorgen
 * dafür, dass (a) auf bewerberseitigen Pfaden weder Session-Recording noch
 * Autocapture läuft und (b) Token-/ID-Segmente in URL-Properties niemals im
 * Klartext bei PostHog landen.
 *
 * Pure Funktionen – unit-getestet in posthog-privacy.test.ts.
 */

/**
 * Bewerberseitige / token-tragende Pfad-Präfixe (public route group):
 * - /consent/*   Einwilligungs-Flow (grant/decline/withdraw/[token], privacy-policy/[rentalCaseId])
 * - /d/*         Dokument-Shortcodes
 * - /viewings/*  Besichtigungs-Flows (cancel/[token], success)
 * - /discover/*  Listing-Discovery ([token], opt-out)
 */
const APPLICANT_PATH_PREFIXES = [
  '/consent',
  '/d',
  '/viewings',
  '/discover',
] as const;

export function isApplicantPath(pathname: string): boolean {
  // Exakt oder mit Segment-Grenze – '/consenting-adults' darf NICHT matchen.
  return APPLICANT_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

/**
 * Token-tragende (nicht-bewerberseitige) Pfade, deren Query komplett verworfen
 * wird (Spec §2.6/F4): die Selbstauskunft-Claim-Page trägt `?token=` als 7-Tage-
 * Capability auf den Vermieter-Absender-Kopf.
 */
const TOKEN_LEAK_PATH_PREFIXES = ['/tools/selbstauskunft/claim'] as const;

function isTokenLeakPath(pathname: string): boolean {
  return TOKEN_LEAK_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

/** Query-Keys, die als Capability-Token gelten und überall gestrippt werden. */
const TOKEN_QUERY_KEYS = ['token', 'template_token'] as const;

/**
 * Entfernt Token-Query-Keys aus einem Query-String (pfad-unabhängig) und
 * strippt sie zusätzlich rekursiv aus einem `next=`-Wert (verschachtelte
 * Auth-Redirect-Kette, z.B. `/auth/sign-up?next=/tools/.../claim?token=…`).
 * Gibt den bereinigten Query-String OHNE führendes `?` zurück (leer = keiner).
 */
function stripTokenQueryKeys(rawQuery: string): string {
  if (!rawQuery) return '';

  const params = new URLSearchParams(rawQuery);
  let changed = false;

  for (const key of TOKEN_QUERY_KEYS) {
    if (params.has(key)) {
      params.delete(key);
      changed = true;
    }
  }

  const next = params.get('next');

  if (next && /[?&](token|template_token)=/.test(next)) {
    const [nextPath, ...nextRest] = next.split('?');
    const cleanedNext = stripTokenQueryKeys(nextRest.join('?'));
    params.set('next', cleanedNext ? `${nextPath}?${cleanedNext}` : nextPath!);
    changed = true;
  }

  if (!changed) return rawQuery;

  return params.toString();
}

/**
 * Autocapture-`url_ignorelist` (Regex-Strings, gematcht gegen volle URL).
 */
export const AUTOCAPTURE_URL_IGNORELIST = [
  '/consent($|/)',
  '/d($|/)',
  '/viewings($|/)',
  '/discover($|/)',
];

/**
 * Maskiert Token-/ID-Segmente in einer URL (oder einem Pfad), bevor sie als
 * Event-/Person-Property erfasst wird. Greift NUR auf bewerberseitigen Pfaden
 * – interne Vermieter-URLs bleiben unverändert (pseudonyme IDs, analytisch
 * nötig, kein Token-Charakter).
 */
export function sanitizeTrackedUrl(url: string): string {
  if (!url) return url;

  // Pfad aus voller URL oder rohem Pfad extrahieren
  let prefix = '';
  let path = url;

  const protoMatch = url.match(/^(https?:\/\/[^/]+)(\/.*)?$/);
  if (protoMatch) {
    prefix = protoMatch[1] ?? '';
    path = protoMatch[2] ?? '/';
  }

  // Query/Fragment abtrennen – auf Bewerber-/Token-Pfaden werden sie BEWUSST
  // verworfen (können Token tragen); auf allen anderen Pfaden werden nur die
  // Token-Query-Keys gestrippt (Rest bleibt analytisch erhalten).
  let rawQuery = '';
  const qIndex = path.search(/[?#]/);
  if (qIndex >= 0) {
    rawQuery = path.charAt(qIndex) === '?' ? path.slice(qIndex + 1) : '';
    path = path.slice(0, qIndex);
  }

  // Nicht-Bewerber-Pfade: Pfad unverändert, aber Token-Query-Keys pfad-unabhängig
  // strippen (deckt /onboarding?template_token= und next-tragende Auth-URLs).
  if (!isApplicantPath(path) && !isTokenLeakPath(path)) {
    const cleanedQuery = stripTokenQueryKeys(rawQuery);
    if (cleanedQuery === rawQuery) {
      return url;
    }
    return `${prefix}${path}${cleanedQuery ? `?${cleanedQuery}` : ''}`;
  }

  const masked = path
    // Selbstauskunft-Claim-Token
    .replace(/^(\/tools\/selbstauskunft\/claim).*/, '$1')
    // Consent-Aktionen mit Token
    .replace(/^(\/consent\/(?:grant|decline|withdraw))\/[^/]+/, '$1/:token')
    // Consent-Privacy-Policy mit rentalCaseId
    .replace(/^(\/consent\/privacy-policy)\/[^/]+/, '$1/:rentalCaseId')
    // Dokument-Shortcodes
    .replace(/^(\/d)\/[^/]+/, '$1/:shortcode')
    // Viewing-Token-Flows
    .replace(/^(\/viewings\/[a-z-]+)\/[^/]+/, '$1/:token')
    // Discover-Token
    .replace(/^(\/discover)\/(?!opt-out$)[^/]+/, '$1/:token');

  // Auf Bewerber-Pfaden Query/Fragment BEWUSST weglassen (kann Token tragen).
  return `${prefix}${masked}`;
}

/** URL-tragende Event-/Person-Property-Keys, die gereinigt werden. */
const URL_PROPERTY_KEYS = [
  '$current_url',
  '$pathname',
  '$referrer',
  '$initial_current_url',
  '$initial_pathname',
  '$initial_referrer',
  '$session_entry_url',
  '$session_entry_pathname',
];

type PostHogEventLike = {
  event?: string;
  properties?: Record<string, unknown>;
  $set?: Record<string, unknown>;
  $set_once?: Record<string, unknown>;
} | null;

function sanitizeRecord(record: Record<string, unknown> | undefined): void {
  if (!record) return;
  for (const key of URL_PROPERTY_KEYS) {
    const value = record[key];
    if (typeof value === 'string') {
      record[key] = sanitizeTrackedUrl(value);
    }
  }
}

/**
 * Reinigt alle URL-Properties eines Events in-place (Event-Properties,
 * $set/$set_once auf Top-Level UND innerhalb der Properties).
 * Gibt das Event zur Weiterverwendung in `before_send` zurück.
 */
export function sanitizeEventUrls<T extends PostHogEventLike>(event: T): T {
  if (!event) return event;
  sanitizeRecord(event.properties);
  sanitizeRecord(event.$set);
  sanitizeRecord(event.$set_once);
  sanitizeRecord(event.properties?.$set as Record<string, unknown> | undefined);
  sanitizeRecord(
    event.properties?.$set_once as Record<string, unknown> | undefined,
  );
  return event;
}

type ExceptionEntry = { type?: unknown; value?: unknown; [k: string]: unknown };

/**
 * Schwärzt PII in Ausnahme-Ereignissen (`$exception`) in-place: Typ, Wert und
 * Meldung in `$exception_list` sowie die Einzelfelder `$exception_message` /
 * `$exception_type`. Frame-Daten (Datei, Zeile, Funktion) bleiben stehen.
 */
export function scrubExceptionEvent<T extends PostHogEventLike>(event: T): T {
  if (!event || event.event !== '$exception') return event;
  const props = event.properties;
  if (!props) return event;

  const list = props.$exception_list;
  if (Array.isArray(list)) {
    for (const entry of list as ExceptionEntry[]) {
      if (!entry || typeof entry !== 'object') continue;
      if (typeof entry.type === 'string') entry.type = piiScrub(entry.type);
      if (typeof entry.value === 'string') entry.value = piiScrub(entry.value);
    }
  }
  for (const key of ['$exception_message', '$exception_type']) {
    const v = props[key];
    if (typeof v === 'string') props[key] = piiScrub(v);
  }
  return event;
}

/**
 * Serverseitig: macht aus einem Fehler eine geschwärzte Kopie (Meldung,
 * Meldungszeile im Stack) und schwärzt die Zusatz-Properties tief; Pfad und
 * Referer laufen zusätzlich durch `sanitizeTrackedUrl` (Token/IDs).
 */
export function scrubServerException(
  error: unknown,
  properties?: Record<string, unknown>,
): { error: unknown; properties: Record<string, unknown> | undefined } {
  let scrubbedError: unknown;
  if (error instanceof Error) {
    const copy = new Error(piiScrub(error.message));
    copy.name = error.name;
    if (error.stack) copy.stack = piiScrubStack(error.stack);
    const digest = (error as Error & { digest?: string }).digest;
    if (digest) (copy as Error & { digest?: string }).digest = digest;
    scrubbedError = copy;
  } else if (typeof error === 'string') {
    scrubbedError = piiScrub(error);
  } else {
    scrubbedError = piiScrubDeep(error);
  }

  if (!properties) return { error: scrubbedError, properties };

  const props = piiScrubDeep(properties);
  const path = properties.path;
  if (typeof path === 'string') props.path = piiScrub(sanitizeTrackedUrl(path));
  const headers = properties.headers;
  if (headers && typeof headers === 'object') {
    const h = props.headers as Record<string, unknown>;
    for (const [name, value] of Object.entries(headers)) {
      if (name.toLowerCase() === 'referer' && typeof value === 'string') {
        h[name] = piiScrub(sanitizeTrackedUrl(value));
      }
    }
  }
  return { error: scrubbedError, properties: props };
}
